import {
  Box,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  getConfirmationServiceKey,
  validateDateRange,
} from "./confirmationUtils";

interface DateConfirmationProps {
  error: any;
  setError?: (value: any) => void;
  formData?: any;
  setFormData: (data: any) => void;
  isDisabled?: any;
  isDoorToDoorAccept: any;
  isFullPackAccept: any;
  setAnyChangeFieldValue?: any;
}

const DateConfirmation = ({
  error,
  setError,
  formData,
  setFormData,
  isDisabled,
  isDoorToDoorAccept,
  isFullPackAccept,
  setAnyChangeFieldValue,
}: DateConfirmationProps) => {
  const { userProfile } = useSelector((state: any) => state?.user);
  const [isTesterSection, setIsTesterSection] = useState(true);
  const {leadDetails} = useSelector((state: any) => state?.lead);
  const isLeadCompletedDisabled = leadDetails?.lead?.status === "COMPLETED" || leadDetails?.lead?.status === "STORAGE";
  useEffect(() => {
    setIsTesterSection(
      userProfile?.features?.includes("Basic Work Diary") || false,
    ); // Sync profile changes
  }, [userProfile]);

  const getServiceKey = (service: any, index: number) =>
    getConfirmationServiceKey(service, index);

  const focusFieldById = (fieldId: string) => {
    const target = document.getElementById(fieldId) as HTMLElement | null;
    if (!target) return false;

    target.focus();
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
  };

  useEffect(() => {
    const serviceErrors = error?.services;
    if (!serviceErrors || !formData?.services?.length) {
      return;
    }

    formData.services.forEach((service: any, index: number) => {
      const serviceKey = getServiceKey(service, index);
      const currentServiceError = serviceErrors?.[serviceKey];

      if (!currentServiceError) {
        return;
      }

      if (currentServiceError?.startDate) {
        if (focusFieldById(`startDate-${serviceKey}`)) return;
      }

      if (currentServiceError?.startTime) {
        if (focusFieldById(`startTime-${serviceKey}`)) return;
      }

      if (currentServiceError?.jobStatus) {
        const radioInput = document.querySelector(
          `input[name="jobStatus-${serviceKey}"]`,
        ) as HTMLInputElement | null;

        if (radioInput) {
          radioInput.focus();
          radioInput.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }
    });
  }, [error, formData?.services]);

  const handleDateChange = (
    service: any,
    index: number,
    field: "startDate" | "startTime" | "endDate" | "endTime",
    value: string | null,
  ) => {
    // Validate end date/time against start date/time
    let validationError: string | null = null;
    const currentService = formData?.services?.[index];

    if (field === "endDate" || field === "startDate") {
      validationError = validateDateRange(
        {
          ...currentService,
          [field]: value,
        },
        field,
        value,
      );
    }

    if (field === "endTime" && value) {
      const isSameDay =
        currentService?.startDate &&
        currentService?.endDate &&
        dayjs(currentService?.endDate).isSame(
          dayjs(currentService?.startDate),
          "day",
        );
      if (
        isSameDay &&
        currentService?.startTime &&
        value <= currentService?.startTime
      ) {
        validationError = "End time must be after the start time";
      }
    }

    const updatedServices = formData.services.map(
      (item: any, itemIndex: number) =>
        itemIndex === index
          ? {
              ...item,
              [field]: value,
            }
          : item,
    );
    setFormData({
      ...formData,
      services: updatedServices,
    });

    const serviceErrorKey = getServiceKey(service, index);
    const errorField = field === "startDate" ? "endDate" : field;

    if (setError) {
      if (validationError) {
        setError((prev: any) => ({
          ...(prev || {}),
          services: {
            ...(prev?.services || {}),
            [serviceErrorKey]: {
              ...(prev?.services?.[serviceErrorKey] || {}),
              [errorField]: validationError,
            },
          },
        }));
        setAnyChangeFieldValue(true);
        return;
      }
    }

    if (value && setError) {
      setError((prev: any) => {
        if (!prev?.services?.[serviceErrorKey]?.[errorField]) return prev;

        const next = { ...prev, services: { ...(prev.services || {}) } };
        const serviceErr = { ...(next.services?.[serviceErrorKey] || {}) };
        delete serviceErr[errorField];

        if (Object.keys(serviceErr).length > 0) {
          next.services[serviceErrorKey] = serviceErr;
        } else {
          delete next.services[serviceErrorKey];
        }

        if (Object.keys(next.services).length === 0) {
          delete next.services;
        }

        return Object.keys(next).length > 0 ? next : null;
      });
    }

    setAnyChangeFieldValue(true);
  };

  const handleStatusChange = (service: any, index: number, value: string) => {
    const updatedServices = formData.services.map(
      (item: any, itemIndex: number) =>
        itemIndex === index
          ? {
              ...item,
              jobStatus: value, // status is mapped to jobStatus
            }
          : item,
    );
    setFormData({
      ...formData,
      services: updatedServices,
    });

    const serviceErrorKey = getServiceKey(service, index);

    if (value && setError) {
      setError((prev: any) => {
        if (!prev?.services?.[serviceErrorKey]?.jobStatus) return prev;

        const next = { ...prev, services: { ...(prev.services || {}) } };
        const serviceErr = { ...(next.services?.[serviceErrorKey] || {}) };
        delete serviceErr.jobStatus;

        if (Object.keys(serviceErr).length > 0) {
          next.services[serviceErrorKey] = serviceErr;
        } else {
          delete next.services[serviceErrorKey];
        }

        if (Object.keys(next.services).length === 0) {
          delete next.services;
        }

        return Object.keys(next).length > 0 ? next : null;
      });
    }

    setAnyChangeFieldValue(true);
  };

  const renderDateSection = (
    label: string,
    service: any,
    isCheck: any,
    index: number,
  ) => {
    const serviceKey = getServiceKey(service, index);
    const serviceErrorKey = serviceKey;

     // Use service-level isWorkerAssigned
  const isServiceWorkerAssigned = service?.isWorkerAssigned === true;

    const canEditStatus =
      formData?.isJobCreated && isCheck && service?.jobStatus !== "fixed";

    const isFieldDisabled =
      formData?.isWorkerAssigned || (isCheck && !canEditStatus);

 const disableJobStatusRadio =
  isServiceWorkerAssigned ||
  (
    formData?.isJobCreated
      ? service?.savedJobStatus === "fixed"
      : (isDisabled || isCheck)
  );

    return (
      <Grid
        key={index}
        item
        xs={12}
        sm={12}
        sx={{
          paddingLeft: "10px !important",
          paddingTop: "6px !important",
          paddingBottom: "8px !important",
          display: "flex",
          alignItems: "flex-start",
          flexWrap: { xs: "wrap", md: "nowrap" },
          columnGap: "8px",
          borderTop: "2px solid #CFCFCF",
          background: "#FFF",
          minHeight: "100px !important",
          overflowX: { xs: "visible", md: "auto" },
        }}
      >
        {/* Start Date + Start Time / End Date + End Time stacked vertically */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "6px",
            width: { xs: "100%", md: "auto" },
          }}
        >
          {/* Start row */}
          <Box
            sx={{
              display: "flex",
              flexWrap: { xs: "wrap", md: "nowrap" },
              columnGap: "8px",
               pointerEvents: isLeadCompletedDisabled ? "none" : "auto",
                opacity: isLeadCompletedDisabled ? 0.6 : 1,
            }}
          >
            <Box
              sx={{
                display: "block",
                width: { xs: "100%", md: "200px" },
                minWidth: { xs: "100%", md: "200px" },
                flexShrink: 0,
                
              }}
            >
              <InputLabel
                sx={{
                  color: "#333333",
                  fontWeight: 600,
                  fontSize: "18px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                shrink
              >
                {label}
                <span style={{ color: "red", fontWeight: 500 }}>*</span>
              </InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  label="Start Date*"
                  value={
                    service?.startDate && dayjs(service?.startDate).isValid()
                      ? dayjs(service?.startDate)
                      : null
                  }
                  format={
                    userProfile?.dateFormat
                      ? userProfile?.dateFormat
                      : "DD/MM/YYYY"
                  }
                  onChange={(newValue: any) => {
                    handleDateChange(
                      service,
                      index,
                      "startDate",
                      newValue ? dayjs(newValue)?.toISOString() : "",
                    );
                  }}
                  disabled={isServiceWorkerAssigned || isCheck}
                  slotProps={{
                    textField: {
                      id: `startDate-${serviceKey}`,
                      name: `startDate-${serviceKey}`,
                      size: "small",
                      placeholder: `${
                        userProfile?.dateFormat
                          ? userProfile?.dateFormat
                          : "DD/MM/YYYY"
                      }`,

                      variant: "outlined",
                      error: !!error?.services?.[serviceErrorKey]?.startDate,
                      helperText:
                        error?.services?.[serviceErrorKey]?.startDate || "",
                      sx: {
                        height: "36px",
                        marginRight: "6px",
                        marginBottom: "6px !important",
                        marginTop: "0px !important",
                        width: "100%",
                      },
                      InputProps: {
                        sx: {
                          height: "36px",
                          width: "100%",
                        },
                      },
                    },
                  }}
                  minDate={
                    service?.type?.toLowerCase() === "removal" ||
                    service.name?.toLowerCase() === "removal" ||
                    service.type?.toLowerCase() === "fullpack" ||
                    service.name?.toLowerCase() === "full pack"
                      ? dayjs()
                      : undefined
                  }
                  maxDate={
                    (service.name?.toLowerCase() === "full pack" ||
                      service.type?.toLowerCase() === "fullpack") &&
                    formData?.services?.find(
                      (s: any) =>
                        s.name?.toLowerCase() === "removal" ||
                        s.type?.toLowerCase() === "removal",
                    )?.startDate
                      ? dayjs(
                          formData?.services?.find(
                            (s: any) =>
                              s.name?.toLowerCase() === "removal" ||
                              s.type?.toLowerCase() === "removal",
                          )?.startDate,
                        )
                      : undefined
                  }
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <TextField
                id={`startTime-${serviceKey}`}
                name={`startTime-${serviceKey}`}
                type="time"
                variant="outlined"
                disabled={isServiceWorkerAssigned || isCheck}
                sx={{ marginRight: "8px", marginTop: "26px !important" }}
                value={service?.startTime ? service?.startTime : ""}
                InputProps={{
                  sx: { height: "36px", padding: "10px 4px", width: "120px" },
                }}
                onChange={(e) =>
                  handleDateChange(service, index, "startTime", e.target.value)
                }
                error={!!error?.services?.[serviceErrorKey]?.startTime}
              />
              {error?.services?.[serviceErrorKey]?.startTime && (
                <FormHelperText
                  sx={{
                    color: "#d32f2f",
                    marginTop: "2px",
                    paddingLeft: "4px",
                    fontSize: "12px",
                    marginLeft: "2px",
                  }}
                >
                  {error.services[serviceErrorKey].startTime}
                </FormHelperText>
              )}
            </Box>
          </Box>

          {/* End row */}
          <Box
            sx={{
              display: "flex",
              flexWrap: { xs: "wrap", md: "nowrap" },
              columnGap: "8px",
               pointerEvents: isLeadCompletedDisabled ? "none" : "auto",
                opacity: isLeadCompletedDisabled ? 0.6 : 1,
            }}
          >
            <Box
              sx={{
                display: "block",
                width: { xs: "100%", md: "200px" },
                minWidth: { xs: "100%", md: "200px" },
                flexShrink: 0,
               
              }}
            >
              {/* <InputLabel
              sx={{
                color: "#333333",
                fontWeight: 600,
                fontSize: "18px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              shrink
            >
              End Date
            </InputLabel> */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  label="End Date"
                  value={
                    service?.endDate && dayjs(service?.endDate).isValid()
                      ? dayjs(service?.endDate)
                      : null
                  }
                  format={
                    userProfile?.dateFormat
                      ? userProfile?.dateFormat
                      : "DD/MM/YYYY"
                  }
                  onChange={(newValue: any) => {
                    handleDateChange(
                      service,
                      index,
                      "endDate",
                      newValue ? dayjs(newValue)?.toISOString() : "",
                    );
                  }}
                  disabled={service?.isNew || isServiceWorkerAssigned || isCheck}
                  minDate={
                    service?.startDate && dayjs(service?.startDate).isValid()
                      ? dayjs(service?.startDate).add(1, "day")
                      : undefined
                  }
                  slotProps={{
                    textField: {
                      id: `endDate-${serviceKey}`,
                      name: `endDate-${serviceKey}`,
                      size: "small",
                      placeholder: `${
                        userProfile?.dateFormat
                          ? userProfile?.dateFormat
                          : "DD/MM/YYYY"
                      }`,
                      variant: "outlined",
                      error: !!error?.services?.[serviceErrorKey]?.endDate,
                      helperText:
                        error?.services?.[serviceErrorKey]?.endDate || "",
                      sx: {
                        height: "36px",
                        marginRight: "6px",
                        marginBottom: "6px !important",
                        marginTop: "0px !important",
                        width: "100%",
                      },
                      InputProps: {
                        sx: {
                          height: "36px",
                          width: "100%",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <TextField
                id={`endTime-${serviceKey}`}
                name={`endTime-${serviceKey}`}
                type="time"
                variant="outlined"
                disabled={service?.isNew ||isServiceWorkerAssigned || isCheck}
                sx={{ marginRight: "8px" }}
                value={service?.endTime ? service?.endTime : ""}
                InputProps={{
                  sx: { height: "36px", padding: "10px 4px", width: "120px" },
                }}
                onChange={(e) =>
                  handleDateChange(service, index, "endTime", e.target.value)
                }
                error={!!error?.services?.[serviceErrorKey]?.endTime}
              />
              {error?.services?.[serviceErrorKey]?.endTime && (
                <FormHelperText
                  sx={{
                    color: "#d32f2f",
                    marginTop: "2px",
                    paddingLeft: "4px",
                    fontSize: "12px",
                    marginLeft: "2px",
                  }}
                >
                  {error.services[serviceErrorKey].endTime}
                </FormHelperText>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            minWidth: "fit-content",
          }}
        >
          <RadioGroup
            row
            name={`jobStatus-${serviceKey}`}
            value={service.jobStatus || ""}
            onChange={(e) => handleStatusChange(service, index, e.target.value)}
            sx={{ flexWrap: "nowrap", marginTop: "20px" }}
          >
            {["fixed", "provisional", "awaiting"].map((status) => (
              <FormControlLabel
                key={status}
                value={status}
                disabled={disableJobStatusRadio}
                // disabled={isDisabled || isCheck}
                sx={{
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  ".MuiTypography-root": {
                    fontSize: "12px",
                  },
                }}
                control={
                  <Radio
                    inputProps={{
                      id: `jobStatus-${serviceKey}-${status}`,
                      name: `jobStatus-${serviceKey}`,
                    }}
                    size="small"
                    sx={{
                      color: error?.services?.[service.name]?.jobStatus
                        ? "red"
                        : "",
                      transform: "scale(0.8)",
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                  />
                }
                label={status.charAt(0).toUpperCase() + status.slice(1)}
              />
            ))}
          </RadioGroup>
          {error?.services?.[serviceErrorKey]?.jobStatus && (
            <FormHelperText
              sx={{ color: "#d32f2f", fontSize: "12px", marginLeft: "2px" }}
            >
              {error.services[serviceErrorKey].jobStatus}
            </FormHelperText>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <>
      <Grid container spacing={2} rowGap={0} rowSpacing={0}>
        <Grid
          item
          xs={12}
          md={12}
          sx={{ padding: "16px 6px 6px 6px !important" }}
        >
          <Typography sx={{ fontWeight: 600, color: "#0F906E" }}>
            Services Date
          </Typography>
        </Grid>
        {(isTesterSection
          ? formData?.services?.filter(
              (service: any) =>
                service.name?.toLowerCase() === "removal" ||
                service.type?.toLowerCase() === "removal" ||
                service.name?.toLowerCase() === "full pack" ||
                service.type?.toLowerCase() === "fullpack",
            )
          : formData?.services
        )?.map((service: any, index: number) =>
          renderDateSection(
            service.name?.toLowerCase() === "removal" ||
              service.type?.toLowerCase() === "removal"
              ? "Moving On"
              : service.name?.toLowerCase() === "full pack" ||
                  service.type?.toLowerCase() === "fullpack"
                ? "Packing Date"
                : `${service.name} Date`,
            service,
            service.status === "accept" ? false : true,
            index,
          ),
        )}
        {/* {renderDateSection("Moving On", "movingDate", isDoorToDoorAccept)}
      {renderDateSection("Packing Start", "packingDate", isFullPackAccept)} */}
      </Grid>
    </>
  );
};

export default DateConfirmation;
